/** The `boards` area: a board: its page, lanes, cards, configuration and
 *  suggestions. Owned by the stage 2 slot flows (docs/I18N.md §11). The keys
 *  read in page order: the head, Add, Refine, the empty board, the suggestion
 *  lane, the lanes, a card, the hidden tray and the toasts, then Configure.
 *  A card's act words match the item page's (the `item` area); they are keyed
 *  again here because a slot keys its own copy. */
export const boards = {
  // --- The head ---
  'boards.page.tasks': { one: '{count} item', other: '{count} items' },
  // --- Add ---
  'boards.add.open': 'Add or create an item on this board',
  'boards.add.panel': 'Add or create an item',
  'boards.add.sharedHint': 'Created here, an item is shared with this board’s audience.',
  // --- Refine: filter and sort ---
  'boards.filter.placeholder': 'Filter this board…',
  'boards.sort.menu': 'Sort by',
  'boards.sort.viewed': 'Last viewed',
  'boards.sort.alpha': 'A–Z',
  'boards.sort.attention': 'Most urgent',
  // --- The empty board ---
  'boards.empty.title': '{board} has nothing on it yet',
  'boards.empty.curated':
    'Add an existing item by searching, or create one - created here is pinned here.',
  'boards.empty.scopeAudience':
    "Create an item, add an existing one, or grant work to this board's audience and it shows up here.",
  'boards.empty.placeholder': 'What needs doing?',
  // --- The suggestion lane ---
  'boards.suggest.strip': {
    one: '{count} match · not pinned',
    other: '{count} matches · not pinned',
  },
  'boards.suggest.pin': 'Pin',
  'boards.suggest.cantAddTitle':
    'You can see this, but adding it here needs owner access to the item.',
  'boards.suggest.cantAdd': 'can’t add here',
  // --- A lane ---
  /** The catch-all lane under each lane source. */
  'boards.lane.unset.flowPhase': 'No stage',
  'boards.lane.unset.manual': 'Unassigned',
  'boards.lane.unset.other': 'Unset',
  'boards.lane.expand': 'Expand {lane}',
  'boards.lane.collapse': 'Collapse {lane}',
  /** A stage lane's meaning: its means pairs. */
  'boards.lane.means': 'means',
  'boards.lane.auto': 'auto · {means}',
  'boards.lane.about': 'About the {lane} column',
  'boards.lane.help':
    'Moving a card here applies these values, and a card reaching them all moves here by itself: {means}. Moving a card away by hand triggers nothing.',
  'boards.lane.add': 'Add to {lane}',
  'boards.lane.compose': 'Add',
  'boards.lane.composePlaceholder': 'Item title...',
  // --- A card ---
  'boards.card.parent': 'Parent · {title}',
  'boards.card.flag': 'Set the health of "{title}"',
  'boards.card.flagButton': 'Set the health of "{title}" (done, blocked)',
  /** The glyph menu's two health states. */
  'boards.card.blocked': 'Blocked',
  'boards.card.open': 'Open',
  /** A lens chip's title: the grade, then where it came from. */
  'boards.card.lensTitle': '{grade} - {provenance}',
  'boards.card.grade.notice': 'notice',
  'boards.card.grade.warning': 'warning',
  'boards.card.grade.critical': 'critical',
  'boards.card.moreSignals': 'More signals - open the item',
  'boards.card.moreActions': 'More actions for "{title}"',
  /** The kebab's acts on the (board, card) pair. */
  'boards.card.openInNewTab': 'Open in new tab',
  'boards.card.copyLink': 'Copy link',
  'boards.card.foldBack': 'Fold back into parent',
  'boards.card.hide': 'Hide from this board',
  'boards.card.unpin': 'Remove from this board',
  /** The sub-items list. */
  'boards.card.pull': 'Pull to board',
  'boards.card.pullItem': 'Pull "{title}" out to the board',
  'boards.card.showLess': 'Show less',
  'boards.card.more': '… {count} more',
  /** The needs-you strip. */
  'boards.card.takeUpForGroup': 'Accept for the group',
  'boards.card.takeUp': 'Accept',
  'boards.card.decline': 'Decline',
  'boards.card.verdictAsk': 'In review: your review',
  'boards.card.accept': 'Accept',
  'boards.card.deny': 'Deny',
  /** The neighbourhood digest count. */
  'boards.card.digestTitle':
    'Statements reaching this item from related work (dependencies and below) - open the item to see them on its relationships',
  'boards.card.digest': '{count} from related work',
  // --- The hidden tray and the toasts ---
  'boards.hidden.count': '{count} hidden',
  'boards.hidden.unhide': 'Unhide',
  'boards.toast.done': 'Done',
  'boards.toast.cancelled': 'Cancelled',
  'boards.toast.undo': 'Undo',
  /** A drop that became an ask: who was asked, then the statement. */
  'boards.toast.asked': '{asked}: {statement}',
  // --- Configure ---
  'boards.config.open': 'Configure board',
  'boards.config.openTitle': 'Configure this board: what the columns mean and its flow link',
  'boards.config.title': 'Configure board',
  'boards.config.cancel': 'Cancel',
  'boards.config.save': 'Save',
  /** Configure's first section on a board: what it is (its name and owner,
   *  the rows of `lenses.configure.*`), and the ways out in the foot. */
  'boards.config.board': 'Board',
  'boards.config.archive': 'Archive this board',
  'boards.config.leave': 'Leave this board',
  'boards.config.restore': 'Restore this board',
  /** What belongs on the board. */
  'boards.config.belongs': 'What belongs on it',
  'boards.config.sourceLabel': 'How the board is populated',
  'boards.config.source.curated': 'Pinned only',
  'boards.config.source.scope': 'Everything granted',
  'boards.config.sourceHint.curated':
    'Pins only: work is added deliberately, and creating here pins the new item.',
  'boards.config.sourceHint.scope': 'Everything granted to this board’s audience, plus pins.',
  /** The attached lens. */
  'boards.config.suggestions': 'Suggestions',
  'boards.config.lensAbout': 'About the attached view',
  'boards.config.lensHelp':
    'An attached view suggests matching work in a quiet column on the board - each viewer sees their own matches, and admitting a suggestion pins it (a logged act). A view never puts anything on the board by itself and never grants access to anything.',
  'boards.config.lensRetired': '{lens} (archived - suggests nothing)',
  'boards.config.detach': 'Detach',
  'boards.config.lensPick': 'View to attach',
  'boards.config.lensNone': 'None (no suggestions)',
  'boards.config.attach': 'Attach',
  /** The lane source. */
  /** The Configure section that holds the kanban's own arrangement. */
  'boards.config.kanban': 'Kanban',
  'boards.config.lanes': 'Columns',
  'boards.config.lanesHint.attribute':
    'Columns are the attribute’s values; moving a card applies that value (logged).',
  'boards.config.lanesHint.flowPhase':
    'Columns are the linked flow’s stages; moving a card advances the item across its boundaries (logged).',
  'boards.config.lanesHint.flowField':
    'Columns are the flow field’s values; moving a card sets the field on the item (logged).',
  'boards.config.lanesHint.manual':
    'Columns are free grouping on this board only; moving a card changes nothing on the item.',
  'boards.config.laneSource': 'Columns by',
  'boards.config.laneSource.flowPhase': 'Flow stages',
  'boards.config.laneSource.field': 'Field: {field}',
  'boards.config.laneSource.manual': 'Manual columns',
  /** An attribute as a lane source, with its values: "Activity (Idle | Active)". */
  'boards.config.laneSource.attribute2': '{attribute} ({first} | {second})',
  'boards.config.laneSource.attribute3': '{attribute} ({first} | {second} | {third})',
  /** The flow link. */
  'boards.config.flow': 'Flow',
  'boards.config.flowAbout': 'About the flow link',
  'boards.config.flowHelp':
    'A board takes its columns from at most one flow - a reference, not ownership: linking changes nothing on the items. The flow itself (stages, conditions, fields) is edited on its own page; re-point or unlink here anytime.',
  'boards.config.flowArchived': '{flow} (archived)',
  'boards.config.flowPick': 'Linked flow',
  'boards.config.flowNone': 'None (no flow)',
  'boards.config.editFlow': 'Edit flow',
  'boards.config.createFlow': '+ Create a flow and link it',
  /** Joining the board's items to the linked flow. */
  'boards.join.button': 'Join this board’s items to {flow}',
  'boards.join.buttonUnnamed': 'Join this board’s items to the flow',
  'boards.join.joined': 'Joined {count}',
  'boards.join.already': '{count} already in the flow',
  'boards.join.skipped': '{count} skipped',
  'boards.join.hiddenTitle': 'An item you can see but not act on',
  'boards.join.forbidden': 'you lack the authority to join it',
  'boards.join.refused': 'the flow refused the join',
  /** The manual lanes. */
  'boards.config.manualLanes': 'Manual columns',
  'boards.config.laneName': 'Column name',
  'boards.config.laneDescription': 'Description (optional)',
  'boards.config.laneDescriptionLabel': 'Column description',
  'boards.config.removeLane': 'Remove column {lane}',
  'boards.config.addLane': '+ Add column',
  /** The board's own health rules. */
  'boards.config.rules': 'Health rules (this board only)',
  'boards.config.rulesAbout': 'About health rules',
  'boards.config.rulesHelp':
    'When a card here carries a fact, this board reads it at the chosen grade - colouring cards on this board only, never written onto the item, invisible everywhere else. Notice is quiet, warning colours and counts, critical is loudest; a board rule only colours and never pages anyone. (Grades on a group’s own catalog do page at critical - that is authored on the catalog, not on a board.)',
  'boards.error.needsConfirmation': 'This needs a confirmation the column cannot ask for.',
} as const;
